/*!
=========================================================
* CradleAI
=========================================================
*/

import { useState, useEffect } from "react";
import Chart from "chart.js";
import { Bar, Pie, Line } from "react-chartjs-2";
import WorldMap from "../components/Maps/WorldMap";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  NavItem,
  NavLink,
  Nav,
  Progress,
  Table,
  Container,
  Row,
  Col,
  Spinner,
} from "reactstrap";

import {
  chartOptions,
  parseOptions,
} from "variables/charts.js";

import Header from "components/Headers/Header.js";
import "../assets/css/dashboard-redesign.css";
import { 
  getDashboardStats,
  getChatRoomMetrics,
  getReferralStats,
  getHackathonOverview,
  getInnovationBoardStats
} from "../api/ApiServices";
import {
  ChatIcon,
  ReferralIcon,
  DashboardIcon,
  CommunityIcon
} from "../components/Icons";
import { useNavigate } from "react-router-dom";

const Index = () => {
  const navigate = useNavigate();
  const [activeNav, setActiveNav] = useState(1);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [countryPage, setCountryPage] = useState(1);
  const ITEMS_PER_PAGE = 6;

  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const isSuperAdmin = userRole === "super admin" || userRole === "superadmin";

  //  Analytics Dashboard States
  const [analyticsTab, setAnalyticsTab] = useState("referral");
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [chatMetrics, setChatMetrics] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [hackathonStats, setHackathonStats] = useState(null);
  const [innovationStats, setInnovationStats] = useState([]);

  const fetchAnalyticsData = async () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const [chatRes, refRes, hackRes, innovRes] = await Promise.all([
        getChatRoomMetrics(),
        getReferralStats(),
        getHackathonOverview(),
        getInnovationBoardStats()
      ]);

      if (chatRes.success) setChatMetrics(chatRes.payload);
      if (refRes.success) setReferralStats(refRes.payload);
      if (hackRes.success) setHackathonStats(hackRes.payload);
      if (innovRes.success) setInnovationStats(innovRes.payload);
    } catch (err) {
      console.error("Error loading analytics data:", err);
      setAnalyticsError("Failed to load dashboard metrics. Please try again.");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  // 👉 Country to Coordinates Mapping for World Map
  const COUNTRY_COORDS = {
    "India": [78.9629, 20.5937],
    "United States": [-95.7129, 37.0902],
    "Australia": [133.7751, -25.2744],
    "Afghanistan": [67.71, 33.9391],
    "American Samoa": [-170.1322, -14.271],
    "South Africa": [24.9916, -28.4793],
    "Nigeria": [8.6753, 9.0820],
    "Kenya": [37.9062, -0.0236],
    "Egypt": [30.8025, 26.8206],
    "Morocco": [-7.0926, 31.7917],
    "United Kingdom": [-3.436, 55.3781],
    "Canada": [-106.3468, 56.1304],
    "Germany": [10.4515, 51.1657],
    "France": [2.2137, 46.2276],
    "Japan": [138.2529, 36.2048],
    "China": [104.1954, 35.8617],
    "Brazil": [-51.9253, -14.235],
    "Argentina": [-63.6167, -38.4161],
    "Unknown": [0, 0]
  };

  const dynamicMarkers = (dashboardStats?.usersByCountry || [])
    .filter(c => COUNTRY_COORDS[c.name])
    .map(c => ({
      name: c.name,
      coordinates: COUNTRY_COORDS[c.name],
      markerOffset: -15
    }));
  const [messageVolumeData, setMessageVolumeData] = useState(
    Array.from({ length: 24 }, (_, i) => ({
      label: `${i.toString().padStart(2, "0")}:00`,
      count: Math.floor(Math.random() * (140 - 80 + 1)) + 80,
    }))
  );
  const [matchesData, setMatchesData] = useState([
    { label: "Public Square", count: 85 },
    { label: "Tech Talk", count: 72 },
    { label: "Music Lounge", count: 64 },
    { label: "Gaming Zone", count: 58 },
    { label: "Movie Club", count: 50 },
    { label: "Art Studio", count: 45 },
    { label: "Foodies", count: 38 },
    { label: "Travelers", count: 32 },
    { label: "Startup Hub", count: 28 },
    { label: "Book worms", count: 22 },
  ]);
  const [countryData, setCountryData] = useState([
    { name: "Nigeria", users: 12450, percent: 45, color: "info", trend: "+2.4%" },
    { name: "South Africa", users: 8200, percent: 30, color: "success", trend: "+1.8%" },
    { name: "Kenya", users: 5100, percent: 15, color: "warning", trend: "-0.5%" },
    { name: "United States", users: 3200, percent: 10, color: "primary", trend: "+3.1%" },
    { name: "United Kingdom", users: 2800, percent: 8, color: "danger", trend: "-1.2%" },
    { name: "India", users: 2500, percent: 7, color: "info", trend: "+5.0%" },
    { name: "Ghana", users: 1800, percent: 5, color: "success", trend: "+0.9%" },
    { name: "Brazil", users: 1500, percent: 4, color: "warning", trend: "-2.1%" },
  ]);

  if (window.Chart) {
    parseOptions(Chart, chartOptions());
  }

  const fetchStats = async () => {
    try {
      const res = await getDashboardStats();
      if (res.status === true) {
        setDashboardStats(res.payload);
      }
    } catch (err) {
      console.error("Failed to fetch dashboard stats", err);
    }
  };

  useEffect(() => {
    fetchStats();
    // Refresh stats every 30 seconds
    const statsInterval = setInterval(fetchStats, 30000);

    // Simulate real-time data updates every 5 seconds for charts
    const interval = setInterval(() => {
      // 1. Update Message Volume
      setMessageVolumeData((prev) => {
        const newData = [...prev];
        const lastIndex = newData.length - 1;
        const currentCount = newData[lastIndex].count;
        const change = Math.floor(Math.random() * 11) - 5;
        newData[lastIndex] = {
          ...newData[lastIndex],
          count: Math.max(0, Math.min(200, currentCount + change)),
        };
        return newData;
      });

      // 2. Update Top Rooms
      setMatchesData((prev) =>
        prev.map((item) => ({
          ...item,
          count: Math.max(0, item.count + (Math.floor(Math.random() * 5) - 2)),
        }))
      );

      // 3. Update Country Data
      setCountryData((prev) =>
        prev.map((item) => ({
          ...item,
          users: Math.max(0, item.users + (Math.floor(Math.random() * 21) - 10)),
        }))
      );
    }, 5000);

    return () => {
      clearInterval(statsInterval);
      clearInterval(interval);
    };
  }, []);

  // 👉 Aggregate 24h data into 8 slots (3 hours each) for a cleaner "static bar" look
  const getAggregatedChartData = () => {
    if (!dashboardStats?.hourlyMessages) return null;
    const slots = [];
    for (let i = 0; i < 24; i += 3) {
      const chunk = dashboardStats.hourlyMessages.slice(i, i + 3);
      const totalCount = chunk.reduce((sum, m) => sum + m.count, 0);
      slots.push({
        label: chunk[0].label,
        count: totalCount,
      });
    }
    return slots;
  };

  const aggregatedData = getAggregatedChartData();

  // Styling and Rendering Helpers for Analytics
  const glassCardStyle = {
    background: "rgba(255, 255, 255, 0.7)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    border: "1px solid rgba(255, 255, 255, 0.4)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.08)",
    transition: "transform 0.3s ease, box-shadow 0.3s ease"
  };

  const renderChatMetrics = () => {
    const totalUsers = chatMetrics.reduce((a, b) => a + (b.activeUsers || 0), 0) || 18482;
    const totalMessages = chatMetrics.reduce((a, b) => a + (b.messageCount || 0), 0) || 142804;

    const lineChartData = {
      labels: chatMetrics.length > 0 ? chatMetrics.map(r => r.name) : ["Global News", "Crypto Feed", "Developers", "Designers", "Marketing"],
      datasets: [
        {
          label: "Message Volume",
          data: chatMetrics.length > 0 ? chatMetrics.map(r => r.messageCount) : [12000, 24000, 18000, 32000, 21000],
          borderColor: "#5e72e4",
          backgroundColor: "rgba(94, 114, 228, 0.05)",
          pointBackgroundColor: "#5e72e4",
          pointBorderColor: "#fff",
          pointHoverBackgroundColor: "#fff",
          pointHoverBorderColor: "#5e72e4",
          pointRadius: 6,
          pointHoverRadius: 8,
          borderWidth: 3,
          tension: 0.4,
          fill: true
        }
      ]
    };

    const trendingList = [
      { topic: "#GenerativeAI", trend: "+18.2%", color: "#2dce89" },
      { topic: "#Blockchain", trend: "+12.5%", color: "#2dce89" },
      { topic: "#Web3", trend: "-2.4%", color: "#f5365c" },
      { topic: "#DesignSystem", trend: "+8.9%", color: "#2dce89" },
      { topic: "#TechStack", trend: "+15.1%", color: "#2dce89" }
    ];

    return (
      <Row>
        {/* Top Summary Cards */}
        <Col xl="4" className="mb-4">
          <Card style={glassCardStyle} className="p-3 text-center">
            <h5 className="text-muted text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "1px" }}>Total Active Users</h5>
            <h2 className="mb-0 font-weight-bold" style={{ color: "#5e72e4" }}>{totalUsers.toLocaleString()}</h2>
            <div className="text-success text-xs mt-1">
              <i className="fa fa-arrow-up" /> +15.3% <span className="text-muted">vs last week</span>
            </div>
          </Card>
        </Col>
        <Col xl="4" className="mb-4">
          <Card style={glassCardStyle} className="p-3 text-center">
            <h5 className="text-muted text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "1px" }}>Total Messages</h5>
            <h2 className="mb-0 font-weight-bold" style={{ color: "#2dce89" }}>{totalMessages.toLocaleString()}</h2>
            <div className="text-success text-xs mt-1">
              <i className="fa fa-arrow-up" /> +24.1% <span className="text-muted">vs last week</span>
            </div>
          </Card>
        </Col>
        <Col xl="4" className="mb-4">
          <Card style={glassCardStyle} className="p-3 text-center">
            <h5 className="text-muted text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "1px" }}>Peak Activity Time</h5>
            <h2 className="mb-0 font-weight-bold" style={{ color: "#11cdef" }}>14:00 - 16:30</h2>
            <div className="text-muted text-xs mt-1">
              <i className="fa fa-clock" /> UTC/GMT timezone
            </div>
          </Card>
        </Col>

        {/* Message Volume Spline Chart */}
        <Col xl="8" className="mb-4">
          <Card style={glassCardStyle}>
            <CardHeader className="bg-transparent border-0 d-flex justify-content-between align-items-center">
              <h4 className="mb-0 font-weight-bold text-primary">Message Volume by Room</h4>
              <span className="badge badge-pill badge-primary">Live Data</span>
            </CardHeader>
            <CardBody>
              <div style={{ height: "300px" }}>
                <Line
                  data={lineChartData}
                  options={{
                    maintainAspectRatio: false,
                    legend: { display: false },
                    scales: {
                      yAxes: [{
                        gridLines: { color: "rgba(0, 0, 0, 0.05)" },
                        ticks: { beginAtZero: true, fontColor: "#8898aa" }
                      }],
                      xAxes: [{
                        gridLines: { display: false },
                        ticks: { fontColor: "#8898aa" }
                      }]
                    }
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Trending Topics */}
        <Col xl="4" className="mb-4">
          <Card style={glassCardStyle} className="h-100">
            <CardHeader className="bg-transparent border-0">
              <h4 className="mb-0 font-weight-bold text-primary">Trending Topics</h4>
            </CardHeader>
            <CardBody className="pt-0">
              <ul className="list-group list-group-flush">
                {trendingList.map((item, idx) => (
                  <li key={idx} className="list-group-item bg-transparent d-flex justify-content-between align-items-center px-0 py-3" style={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                    <span className="font-weight-bold text-dark">{item.topic}</span>
                    <span className="badge badge-pill font-weight-bold" style={{ backgroundColor: `${item.color}15`, color: item.color, fontSize: "11px" }}>
                      {item.trend}
                    </span>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        </Col>

        {/* Room-by-Room Breakdown Table */}
        <Col xl="12" className="mb-4">
          <Card style={glassCardStyle}>
            <CardHeader className="bg-transparent border-0">
              <h4 className="mb-0 font-weight-bold text-primary">Room-by-Room Breakdown</h4>
            </CardHeader>
            <CardBody className="p-0">
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th>Room Name</th>
                    <th>Active Users</th>
                    <th>Engagement Rate</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {chatMetrics.length > 0 ? (
                    chatMetrics.map((room, i) => {
                      const maxMsgs = Math.max(...chatMetrics.map(r => r.messageCount), 1);
                      const percent = Math.min(100, Math.round((room.messageCount / maxMsgs) * 100));
                      return (
                        <tr key={i}>
                          <td className="font-weight-bold text-dark">{room.name}</td>
                          <td>
                            <i className="fa fa-users text-muted mr-2" />
                            {room.activeUsers} users
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <span className="mr-2">{room.messageCount} msgs</span>
                              <Progress max="100" value={percent} color="primary" style={{ height: "6px", width: "100px" }} />
                            </div>
                          </td>
                          <td>
                            <span className="badge badge-dot mr-4">
                              <i className="bg-success" />
                              <span className="status">active</span>
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center py-4 text-muted">No rooms active</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderReferrals = () => {
    if (!referralStats) return <div className="text-center py-5">No stats loaded</div>;

    const totalReferrals = referralStats.totalReferrals || 0;
    const crossCountryCount = referralStats.crossCountryCount || 0;
    const domesticCount = Math.max(0, totalReferrals - crossCountryCount);
    const domesticPercent = totalReferrals > 0 ? Math.round((domesticCount / totalReferrals) * 100) : 0;
    const crossCountryPercent = totalReferrals > 0 ? Math.round((crossCountryCount / totalReferrals) * 100) : 0;

    const pieData = {
      labels: ["Domestic Invites", "Cross-Country Invites"],
      datasets: [
        {
          data: [domesticCount, crossCountryCount],
          backgroundColor: ["#5e72e4", "#2dce89"],
          hoverBackgroundColor: ["#324cdd", "#2dcecc"]
        }
      ]
    };

    const funnelSteps = [
      { name: "Referrals Initiated", percent: 100 },
      { name: "Signups Completed", percent: 65 },
      { name: "Active Conversions", percent: 45 },
      { name: "Reward Disbursed", percent: 35 }
    ];

    return (
      <Row>
        <Col xl="6" className="mb-4">
          <Card style={glassCardStyle} className="h-100">
            <CardHeader className="bg-transparent border-0 pb-1">
              <h4 className="mb-0 font-weight-bold text-dark">Invite Distribution</h4>
            </CardHeader>
            <CardBody className="pt-0">
              <Row className="align-items-center" style={{ minHeight: "280px" }}>
                <Col sm="6" className="d-flex justify-content-center py-3">
                  <div style={{ width: "200px", height: "200px" }}>
                    <Pie data={pieData} options={{ maintainAspectRatio: false, legend: { display: false } }} />
                  </div>
                </Col>
                <Col sm="6" className="d-flex flex-column justify-content-center">
                  <div className="mb-3">
                    <span className="text-muted text-uppercase font-weight-bold text-xs" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>Total Referrals</span>
                    <h3 className="mb-0 font-weight-bold text-primary">{totalReferrals.toLocaleString()}</h3>
                  </div>
                  <div className="mb-2 d-flex align-items-center">
                    <span className="badge badge-dot mr-2">
                      <i style={{ backgroundColor: "#5e72e4" }} />
                    </span>
                    <div className="text-xs">
                      <strong className="text-dark">Domestic:</strong> {domesticCount.toLocaleString()} ({domesticPercent}%)
                    </div>
                  </div>
                  <div className="mb-3 d-flex align-items-center">
                    <span className="badge badge-dot mr-2">
                      <i style={{ backgroundColor: "#2dce89" }} />
                    </span>
                    <div className="text-xs">
                      <strong className="text-dark">Cross-Country:</strong> {crossCountryCount.toLocaleString()} ({crossCountryPercent}%)
                    </div>
                  </div>
                  <hr className="my-2" style={{ borderColor: "rgba(0,0,0,0.05)" }} />
                  <div className="row">
                    <div className="col-6 mb-2">
                      <span className="text-muted text-uppercase text-xs d-block" style={{ fontSize: "9px" }}>Monetary Rewards</span>
                      <strong className="text-success text-sm">${referralStats.totalRewardMoney?.toLocaleString()}</strong>
                    </div>
                    <div className="col-6 mb-2">
                      <span className="text-muted text-uppercase text-xs d-block" style={{ fontSize: "9px" }}>Points Issued</span>
                      <strong className="text-warning text-sm">{referralStats.totalRewardPoints?.toLocaleString()} pts</strong>
                    </div>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        <Col xl="6" className="mb-4">
          <Card style={glassCardStyle} className="h-100">
            <CardHeader className="bg-transparent border-0 pb-1">
              <h4 className="mb-0 font-weight-bold text-dark">Referral Funnel & Progress</h4>
            </CardHeader>
            <CardBody className="d-flex flex-column justify-content-center pb-4" style={{ minHeight: "280px" }}>
              {funnelSteps.map((step, idx) => (
                <div key={idx} className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="font-weight-bold text-dark text-xs">{step.name}</span>
                    <span className="text-muted text-xs font-weight-bold">{step.percent}%</span>
                  </div>
                  <Progress max="100" value={step.percent} color="primary" style={{ height: "8px", borderRadius: "4px" }} />
                </div>
              ))}
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderHackathons = () => {
    if (!hackathonStats) return <div className="text-center py-5">No stats loaded</div>;

    const totalChallenges = hackathonStats.overview?.length || 12;
    const totalSubs = (hackathonStats.overview || []).reduce((a, s) => a + s.totalSubmissions, 0) || 1482;
    const reviewRate = "64%";
    const avgScore = 8.4;

    const recentSubmissionsList = hackathonStats.recentSubmissions && hackathonStats.recentSubmissions.length > 0
      ? hackathonStats.recentSubmissions
      : [
          { title: "Eco Tracker", user: "Alex Rivera", challenge: "COMMUNITY", score: 9.2, status: "winner", date: new Date() },
          { title: "HealthSync Hub", user: "Sarah Chen", challenge: "HACKATHON", score: 8.8, status: "reviewed", date: new Date() },
          { title: "RealPay Ledger", user: "Nitin Sharma", challenge: "FINTECH", score: 8.5, status: "pending", date: new Date() }
        ];

    const criteriaList = [
      { name: "Technical Complexity", avg: "8.5", percent: 85, color: "#5e72e4" },
      { name: "Innovation & Originality", avg: "9.0", percent: 90, color: "#2dce89" },
      { name: "Market Potential", avg: "7.5", percent: 75, color: "#ff9f43" },
      { name: "Design & UX", avg: "8.0", percent: 80, color: "#11cdef" }
    ];

    return (
      <Row>
        {/* Top Summary Cards */}
        <Col xl="3" className="mb-4">
          <Card style={glassCardStyle} className="p-3 text-center">
            <h5 className="text-muted text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "1px" }}>Total Challenges</h5>
            <h2 className="mb-0 font-weight-bold" style={{ color: "#5e72e4" }}>{totalChallenges}</h2>
            <div className="text-success text-xs mt-1">
              <i className="fa fa-arrow-up" /> +2 new <span className="text-muted">this week</span>
            </div>
          </Card>
        </Col>
        <Col xl="3" className="mb-4">
          <Card style={glassCardStyle} className="p-3 text-center">
            <h5 className="text-muted text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "1px" }}>Submissions</h5>
            <h2 className="mb-0 font-weight-bold" style={{ color: "#2dce89" }}>{totalSubs.toLocaleString()}</h2>
            <div className="text-success text-xs mt-1">
              <i className="fa fa-arrow-up" /> +14.2% <span className="text-muted">vs last week</span>
            </div>
          </Card>
        </Col>
        <Col xl="3" className="mb-4">
          <Card style={glassCardStyle} className="p-3 text-center">
            <h5 className="text-muted text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "1px" }}>Review Rate</h5>
            <h2 className="mb-0 font-weight-bold" style={{ color: "#ff9f43" }}>{reviewRate}</h2>
            <div className="text-success text-xs mt-1">
              <i className="fa fa-arrow-up" /> +3.4% <span className="text-muted">vs last week</span>
            </div>
          </Card>
        </Col>
        <Col xl="3" className="mb-4">
          <Card style={glassCardStyle} className="p-3 text-center">
            <h5 className="text-muted text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "1px" }}>Avg. Score</h5>
            <h2 className="mb-0 font-weight-bold" style={{ color: "#f5365c" }}>{avgScore}</h2>
            <div className="text-success text-xs mt-1">
              <i className="fa fa-check" /> Standardized <span className="text-muted">scale</span>
            </div>
          </Card>
        </Col>

        {/* Judging Criteria */}
        <Col xl="12" className="mb-4">
          <Card style={glassCardStyle} className="h-100">
            <CardHeader className="bg-transparent border-0">
              <h4 className="mb-0 font-weight-bold text-primary">Judging Criteria</h4>
            </CardHeader>
            <CardBody className="pt-0">
              {criteriaList.map((criteria, idx) => (
                <div key={idx} className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="font-weight-bold text-dark text-xs">{criteria.name}</span>
                    <span className="text-muted text-xs font-weight-bold">Avg. {criteria.avg}</span>
                  </div>
                  <Progress max="100" value={criteria.percent} style={{ height: "6px", borderRadius: "3px" }} barStyle={{ backgroundColor: criteria.color }} />
                </div>
              ))}
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderInnovationBoard = () => {
    const totalIdeas = innovationStats.length || 0;
    const totalUpvotes = innovationStats.reduce((acc, curr) => acc + (curr.upvotes || 0), 0);
    const totalComments = innovationStats.reduce((acc, curr) => acc + (curr.comments || 0), 0);
    const totalFundingNeeded = innovationStats.reduce((a, i) => a + (i.fundingNeeded || 0), 0);
    const totalFundingReceived = innovationStats.reduce((a, i) => a + (i.fundingReceived || 0), 0);

    const upvotePieData = {
      labels: innovationStats.slice(0, 5).map(idea => idea.title.length > 25 ? idea.title.substring(0, 25) + "..." : idea.title),
      datasets: [
        {
          data: innovationStats.slice(0, 5).map(idea => idea.upvotes || 0),
          backgroundColor: ["#5e72e4", "#2dce89", "#11cdef", "#fb6340", "#f5365c"],
          hoverBackgroundColor: ["#324cdd", "#2dcecc", "#11b3d8", "#f84f2c", "#ec1c44"]
        }
      ]
    };

    const fundingBarData = {
      labels: innovationStats.slice(0, 5).map(idea => idea.title.length > 20 ? idea.title.substring(0, 20) + "..." : idea.title),
      datasets: [
        {
          label: "Funding Received",
          data: innovationStats.slice(0, 5).map(idea => idea.fundingReceived || 0),
          backgroundColor: "#2dce89",
          borderRadius: 4
        },
        {
          label: "Funding Target",
          data: innovationStats.slice(0, 5).map(idea => idea.fundingNeeded || 0),
          backgroundColor: "rgba(0, 0, 0, 0.05)",
          borderRadius: 4
        }
      ]
    };

    return (
      <Row>
        {/* Top Cards */}
        <Col xl="3" className="mb-4">
          <Card style={glassCardStyle} className="p-3 text-center">
            <h5 className="text-muted text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "1px" }}>Total Ideas</h5>
            <h2 className="mb-0 font-weight-bold" style={{ color: "#5e72e4" }}>{totalIdeas}</h2>
          </Card>
        </Col>
        <Col xl="3" className="mb-4">
          <Card style={glassCardStyle} className="p-3 text-center">
            <h5 className="text-muted text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "1px" }}>Total Upvotes</h5>
            <h2 className="mb-0 font-weight-bold" style={{ color: "#2dce89" }}>{totalUpvotes.toLocaleString()}</h2>
          </Card>
        </Col>
        <Col xl="3" className="mb-4">
          <Card style={glassCardStyle} className="p-3 text-center">
            <h5 className="text-muted text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "1px" }}>Total Comments</h5>
            <h2 className="mb-0 font-weight-bold" style={{ color: "#ff9f43" }}>{totalComments.toLocaleString()}</h2>
          </Card>
        </Col>
        <Col xl="3" className="mb-4">
          <Card style={glassCardStyle} className="p-3 text-center">
            <h5 className="text-muted text-uppercase mb-1" style={{ fontSize: "11px", letterSpacing: "1px" }}>Funding Progress</h5>
            <h2 className="mb-0 font-weight-bold" style={{ color: "#f5365c" }}>${totalFundingReceived.toLocaleString()} / ${totalFundingNeeded.toLocaleString()}</h2>
          </Card>
        </Col>

        {/* Charts */}
        <Col xl="6" className="mb-4">
          <Card style={glassCardStyle}>
            <CardHeader className="bg-transparent border-0">
              <h4 className="mb-0 font-weight-bold text-primary">Engagement by Idea (Upvotes)</h4>
            </CardHeader>
            <CardBody className="d-flex align-items-center justify-content-center" style={{ height: "300px" }}>
              <div style={{ width: "250px", height: "250px" }}>
                <Pie data={upvotePieData} options={{ maintainAspectRatio: false }} />
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl="6" className="mb-4">
          <Card style={glassCardStyle}>
            <CardHeader className="bg-transparent border-0">
              <h4 className="mb-0 font-weight-bold text-primary">Funding Target Progress</h4>
            </CardHeader>
            <CardBody style={{ height: "300px" }}>
              <Bar 
                data={fundingBarData} 
                options={{ 
                  maintainAspectRatio: false,
                  scales: {
                    yAxes: [{
                      ticks: {
                        beginAtZero: true,
                        callback: function(value) { return '$' + value.toLocaleString(); }
                      }
                    }]
                  }
                }} 
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  };

  //  Format chart data for Dark Bar Chart (Message Volume)
  const darkBarChartData = {
    labels: aggregatedData?.map(m => m.label) || ["00:00", "04:00", "08:00", "12:00", "16:00", "20:00", "23:59"],
    datasets: [
      {
        label: "Volume",
        data: aggregatedData?.map(m => m.count) || [30, 45, 65, 55, 75, 95, 60],
        backgroundColor: aggregatedData?.map((m, i) => {
          const maxCount = Math.max(...aggregatedData.map(d => d.count));
          return m.count === maxCount && maxCount > 0 ? "#0084ff" : "rgba(255,255,255,0.1)";
        }) || [
            "rgba(255,255,255,0.1)",
            "rgba(255,255,255,0.1)",
            "rgba(255,255,255,0.1)",
            "rgba(255,255,255,0.1)",
            "rgba(255,255,255,0.1)",
            "#0084ff",
            "rgba(255,255,255,0.1)",
          ],
        borderRadius: 5,
        borderWidth: 0,
      },
    ],
  };

  //  Dashboard Skeleton Component
  const DashboardSkeleton = () => (
    <div className="p-4" style={{ backgroundColor: '#f8f9fe', minHeight: '100vh' }}>
      <div className="mb-4">
        <div className="skeleton-title skeleton-shimmer" style={{ width: '40%', height: '3rem' }} />
        <div className="skeleton-text skeleton-shimmer" style={{ width: '60%' }} />
      </div>
      <Row className="mb-4">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Col key={i} lg="4" xl="2">
            <div className="skeleton-card skeleton-shimmer" />
          </Col>
        ))}
      </Row>
      <Row>
        <Col xl="8">
          <div className="skeleton-chart skeleton-shimmer" style={{ backgroundColor: '#111319' }} />
        </Col>
        <Col xl="4">
          <div className="skeleton-card skeleton-shimmer h-100" style={{ minHeight: '400px' }} />
        </Col>
      </Row>
    </div>
  );

  // Hot Rooms Data
  const defaultHotRooms = [
    { name: "Global News", value: "24.2k ops/m", percent: 90, color: "#0084ff" },
    { name: "Crypto Feed", value: "21.8k ops/m", percent: 80, color: "#0084ff" },
    { name: "Editorial QA", value: "19.5k ops/m", percent: 70, color: "#6ea8fe" },
    { name: "Main Lobby", value: "15.1k ops/m", percent: 55, color: "#adb5bd" },
    { name: "Dev Support", value: "12.9k ops/m", percent: 45, color: "#adb5bd" },
    { name: "Room #402", value: "ANOMALOUS SPIKE", percent: 100, color: "#f5c2c7", textColor: "#f5365c" },
  ];

  const hotRooms = dashboardStats?.hotRooms?.length > 0 ? dashboardStats.hotRooms : defaultHotRooms;

  if (!dashboardStats) {
    return <DashboardSkeleton />;
  }

  return (
    <>
      <Header stats={dashboardStats} />
      {/* Page content */}
      <Container className="mt--7" fluid>
        <Row>
          <Col xl="8" className="mb-4 mb-xl-0">
            <Card className="chart-container-dark shadow border-0">
              <CardHeader className="bg-transparent border-0 d-flex align-items-center justify-content-between p-4">
                <div>
                  <h6 className="text-uppercase text-light ls-1 mb-1">Overview</h6>
                  <h2 className="text-white mb-0">Message Volume (24h)</h2>
                </div>
                <div className="live-flow-indicator">
                  <div className="live-flow-dot" /> Live Flow
                </div>
              </CardHeader>
              <CardBody>
                <div className="chart" style={{ height: "350px" }}>
                  <Bar
                    data={darkBarChartData}
                    options={{
                      maintainAspectRatio: false,
                      legend: { display: false },
                      scales: {
                        yAxes: [{
                          display: false,
                          ticks: { beginAtZero: true }
                        }],
                        xAxes: [{
                          gridLines: { display: false },
                          ticks: { fontColor: "rgba(255,255,255,0.5)", fontSize: 10 }
                        }]
                      }
                    }}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl="4">
            <Card className="shadow border-0 h-100 rounded-24">
              <CardHeader className="bg-transparent border-0 pb-2">
                <div className="d-flex justify-content-between align-items-center">
                  <h3 className="mb-0 font-weight-bold">Critical Alerts</h3>
                  <span className="badge badge-soft-danger text-uppercase px-2 py-1" style={{ backgroundColor: '#fff5f5', color: '#f5365c', fontSize: '0.65rem' }}>Action Required</span>
                </div>
              </CardHeader>
              <CardBody className="pt-0">
                <div className="alert-item critical">
                  <div className="d-flex align-items-center mb-1">
                    <i className="fas fa-exclamation-triangle text-danger mr-2" />
                    <h5 className="mb-0 font-weight-bold">Suspicious Transactions</h5>
                  </div>
                  <p className="text-xs text-muted mb-2">3 flagged items detected in Wallet Volume.</p>
                  <a
                    href="#pablo"
                    className="text-xs font-weight-bold text-uppercase text-danger"
                    onClick={(e) => {
                      e.preventDefault();
                      if (isSuperAdmin) {
                        navigate("/admin/system-logs");
                      }
                    }}
                    style={!isSuperAdmin ? { pointerEvents: "none", opacity: 0.5, cursor: "default" } : {}}
                  >
                    Review Audit Log
                  </a>
                </div>

                <div
                  className="alert-item warning"
                  style={{ borderLeftColor: '#0084ff', cursor: isSuperAdmin ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (isSuperAdmin) {
                      localStorage.setItem("mod_tab", "user");
                      navigate("/admin/moderation");
                    }
                  }}
                >
                  <div className="d-flex align-items-center mb-1">
                    <i className="fas fa-chart-line text-primary mr-2" style={{ color: '#0084ff !important' }} />
                    <h5 className="mb-0 font-weight-bold">Spike in Reports</h5>
                  </div>
                  <p className="text-xs text-muted mb-2">+15% surge in moderation reports for Room #402.</p>
                  <a
                    href="#pablo"
                    onClick={(e) => {
                      e.preventDefault();
                      if (isSuperAdmin) {
                        localStorage.setItem("mod_tab", "user");
                        navigate("/admin/moderation");
                      }
                    }}
                    className="text-xs font-weight-bold text-uppercase text-primary"
                    style={!isSuperAdmin ? { pointerEvents: "none", opacity: 0.5, cursor: "default" } : {}}
                  >
                    Monitor Room
                  </a>
                </div>

                <div
                  className="alert-item info"
                  style={{ borderLeftColor: '#8898aa', cursor: isSuperAdmin ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (isSuperAdmin) {
                      navigate("/admin/moderation");
                    }
                  }}
                >
                  <div className="d-flex align-items-center mb-1">
                    <i className="fas fa-robot text-muted mr-2" />
                    <h5 className="mb-0 font-weight-bold">AI-Flagged Content</h5>
                  </div>
                  <p className="text-xs text-muted mb-2">12 items pending automated review approval.</p>
                  <a
                    href="#pablo"
                    onClick={(e) => {
                      e.preventDefault();
                      if (isSuperAdmin) {
                        navigate("/admin/moderation");
                      }
                    }}
                    className="text-xs font-weight-bold text-uppercase text-muted"
                    style={!isSuperAdmin ? { pointerEvents: "none", opacity: 0.5, cursor: "default" } : {}}
                  >
                    Open Queue
                  </a>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-5">
          <Col xl="6">
            <Card className="shadow border-0 h-100">
              <CardHeader className="bg-transparent border-0 pb-0">
                <Row className="align-items-center">
                  <div className="col">
                    <h4 className="mb-0 text-primary font-weight-bold">Active Users by Country</h4>
                  </div>
                  <div className="col text-right">
                    <span className="badge badge-soft-primary px-3 py-1" style={{ backgroundColor: '#e8f0fe', color: '#1a73e8', borderRadius: '15px' }}>
                      <i className="fas fa-globe mr-1" /> Live Node Map
                    </span>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                <WorldMap markers={dynamicMarkers} />
                <div className="mt-4">
                  {(() => {
                    const allCountries = dashboardStats?.usersByCountry || [];
                    const startIndex = (countryPage - 1) * ITEMS_PER_PAGE;
                    const paginatedCountries = allCountries.slice(startIndex, startIndex + ITEMS_PER_PAGE);
                    const totalPages = Math.ceil(allCountries.length / ITEMS_PER_PAGE);

                    if (allCountries.length > 0) {
                      return (
                        <>
                          {paginatedCountries.map((country, idx) => (
                            <div className={idx === paginatedCountries.length - 1 ? "mb-0" : "mb-3"} key={country.name}>
                              <div className="d-flex justify-content-between mb-1">
                                <span className="text-sm font-weight-600">{country.name}</span>
                                <span className={`text-sm font-weight-bold text-${country.color || 'primary'}`}>{country.percent}%</span>
                              </div>
                              <Progress max="100" value={country.percent} color={country.color} style={{ height: '6px', borderRadius: '10px' }} />
                            </div>
                          ))}

                          {totalPages > 1 && (
                            <div className="d-flex justify-content-center align-items-center mt-4">
                              <Button
                                color="primary"
                                outline
                                size="sm"
                                disabled={countryPage === 1}
                                onClick={() => setCountryPage(prev => prev - 1)}
                                className="mr-3"
                                style={{ borderRadius: '10px', padding: '4px 12px' }}
                              >
                                <i className="fas fa-chevron-left" />
                              </Button>
                              <span className="text-xs font-weight-bold text-muted">
                                Page {countryPage} of {totalPages}
                              </span>
                              <Button
                                color="primary"
                                outline
                                size="sm"
                                disabled={countryPage === totalPages}
                                onClick={() => setCountryPage(prev => prev + 1)}
                                className="ml-3"
                                style={{ borderRadius: '10px', padding: '4px 12px' }}
                              >
                                <i className="fas fa-chevron-right" />
                              </Button>
                            </div>
                          )}
                        </>
                      );
                    } else {
                      return <div className="text-center text-muted py-4">No country data available</div>;
                    }
                  })()}
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl="6">
            <Card className="shadow border-0 h-100">
              <CardHeader className="bg-transparent border-0 pb-0">
                <Row className="align-items-center">
                  <div className="col">
                    <h4 className="mb-0 text-primary font-weight-bold">Top 10 Groups by Activity</h4>
                    <p className="text-xs text-muted mb-0 mt-1">Live message volume, active users & trending topics</p>
                  </div>
                  <div className="col text-right">
                    <span className="badge badge-soft-info px-3 py-1" style={{ backgroundColor: '#e8f0fe', color: '#1a73e8', borderRadius: '15px', textTransform: 'uppercase', fontSize: '10px' }}>
                      Live Data
                    </span>
                  </div>
                </Row>
              </CardHeader>
              <CardBody style={{ overflowY: "auto", maxHeight: "520px" }}>
                {(() => {
                  const top10 = chatMetrics.slice(0, 10);
                  const maxMsgs = top10.length > 0 ? Math.max(...top10.map(g => g.messageCount), 1) : 1;
                  const barColors = ["#667eea", "#764ba2", "#0084ff", "#6ea8fe", "#38b2ac", "#ed64a6", "#ecc94b", "#48bb78", "#4299e1", "#9f7aea"];

                  if (top10.length === 0) {
                    return (
                      <div className="text-center py-5 text-muted">
                        <i className="fas fa-chart-bar fa-2x mb-3" style={{ opacity: 0.3 }} />
                        <p>No group activity data available yet.</p>
                      </div>
                    );
                  }

                  return top10.map((group, idx) => {
                    const percent = Math.round((group.messageCount / maxMsgs) * 100);
                    const color = barColors[idx % barColors.length];

                    return (
                      <div key={idx} className="mb-3 p-3" style={{ 
                        background: "rgba(255,255,255,0.5)", 
                        borderRadius: "12px", 
                        border: "1px solid rgba(0,0,0,0.04)",
                        transition: "all 0.2s ease"
                      }}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className="room-label mb-0 font-weight-bold" style={{ fontSize: "13px", color: "#2d3748" }}>
                            {group.name}
                          </span>
                          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                            <span className="text-xs" style={{ color: "#38b2ac" }}>
                              <i className="fas fa-users mr-1" />{group.activeUsers} users
                            </span>
                            <span className="text-xs font-weight-bold" style={{ color }}>
                              {group.messageCount} msgs
                            </span>
                          </div>
                        </div>

                        <div className="hot-room-bar">
                          <div
                            className="hot-room-progress"
                            style={{
                              width: `${percent}%`,
                              backgroundColor: color,
                              color: "white",
                              transition: "width 0.6s ease"
                            }}
                          />
                        </div>

                        {group.trendingTopics && group.trendingTopics.length > 0 && (
                          <div className="d-flex flex-wrap mt-2" style={{ gap: "4px" }}>
                            {group.trendingTopics.map((topic, tIdx) => (
                              <span key={tIdx} className="badge" style={{ 
                                fontSize: "10px", 
                                padding: "3px 8px", 
                                background: `${color}15`,
                                color: color,
                                borderRadius: "10px",
                                border: `1px solid ${color}30`,
                                fontWeight: "600"
                              }}>
                                {topic}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Index;
